/**
 * Projects the active question's — or the results screen's — corner icons onto the quiz form.
 *
 * The icons are authored per block, so they are rendered inside their own slide
 * where block.settings is in scope. They cannot stay there: the slide sits inside
 * the swiper, whose wrapper carries a transform, and a transformed ancestor becomes
 * the containing block for absolutely positioned descendants — so the swiper's
 * overflow: hidden clips them however they are positioned. Nothing rendered inside
 * a slide can reach the corners of .quiz__form.
 *
 * This element sits outside both the swiper and the card, so the clone anchors to
 * .quiz__frame and may overhang the card instead of being clipped by it.
 *
 * The active slide is read from the Swiper instance's own activeIndex rather than by
 * querying .swiper-slide-active: that class is only a rendering artefact, and any
 * leftover copy of it on another slide makes the query return the wrong icon set
 * for every slide — which reads as one question's icons being stuck on all of them.
 * Swiper's events are the primary trigger for the same reason; the class observer
 * stays as the signal that the instance now exists (it is created on intersection,
 * well after this element connects) and as a fallback while it does not.
 *
 * The results block authors its own set the same way, marked [data-quiz-results-icons].
 * It sits outside the swiper already but still inside .quiz__section's overflow: hidden,
 * so it goes through the same projection; [results-page] toggling d-none is what tells
 * us it has taken over from the questions.
 */
class QuizCornerIcons extends HTMLElement {
  connectedCallback() {
    this.frame = this.closest('.quiz__frame');
    this.carousel = this.frame?.querySelector('quiz-carousel-component');
    this.wrapper = this.frame?.querySelector('[main-slider] .swiper-wrapper');
    if (!this.carousel || !this.wrapper) return;

    // Cached at connect time: the clone this element appends carries the same marker
    // attributes, so re-querying later could pick the projection instead of the source.
    this.quizForm = this.frame.querySelector('quiz-form');
    this.landing = this.quizForm?.querySelector('[quiz-start]');
    this.resultsPage = this.frame.querySelector('[results-page]');
    this.resultsIcons = this.frame.querySelector('[data-quiz-results-icons]');

    this.handleChange = () => {
      this.attachToSwiper();
      this.sync();
    };

    this.observer = new MutationObserver(this.handleChange);
    // On the carousel element for the carousel-active class it gains once the Swiper
    // instance exists, and on the slides for their active class.
    this.observer.observe(this.carousel, { attributes: true, attributeFilter: ['class'] });
    this.observer.observe(this.wrapper, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
    // quiz__start lands on <quiz-form> when the welcome screen is dismissed.
    if (this.quizForm) {
      this.observer.observe(this.quizForm, { attributes: true, attributeFilter: ['class'] });
    }
    if (this.resultsPage) {
      this.observer.observe(this.resultsPage, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    this.handleChange();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.swiper?.off('slideChange', this.boundSync);
    this.swiper?.off('slideChangeTransitionEnd', this.boundSync);
    this.swiper = null;
  }

  /** Binds to the Swiper instance the first time it shows up. */
  attachToSwiper() {
    const instance = this.carousel.swiperInstance;
    if (!instance || instance === this.swiper || instance.destroyed) return;
    this.swiper = instance;
    this.boundSync = () => this.sync();
    instance.on('slideChange', this.boundSync);
    instance.on('slideChangeTransitionEnd', this.boundSync);
  }

  /**
   * The results screen wins while it is visible, otherwise the active question — and
   * nothing at all while the welcome screen is up.
   *
   * The carousel is initialised behind that welcome screen (the form is only faded to
   * visibility: hidden, so the first slide is already the active one) and the results
   * screen replaces the form rather than living inside it. Both would otherwise leak
   * the first question's icons onto a screen that has none of its own.
   */
  get activeSource() {
    const onResults = this.resultsPage && !this.resultsPage.classList.contains('d-none');
    if (onResults) return this.resultsIcons;
    if (this.landing && !this.quizForm.classList.contains('quiz__start')) return null;

    const slide = this.swiper && !this.swiper.destroyed
      ? this.swiper.slides?.[this.swiper.activeIndex]
      : this.wrapper.querySelector('.swiper-slide-active');
    return slide?.querySelector('[data-quiz-corner-icons]') || null;
  }

  sync() {
    const source = this.activeSource;
    const key = source?.getAttribute('data-quiz-corner-icons') || '';
    if (key === this.renderedKey) return;
    this.renderedKey = key;

    this.replaceChildren();
    if (!source) return;

    const clone = source.cloneNode(true);
    clone.removeAttribute('hidden');
    clone.removeAttribute('data-quiz-results-icons');
    // The reveal animation holds its target at opacity 0 until an observer fires on
    // it. A fresh clone is never observed, so unwrap it — otherwise the icons land
    // in the DOM correctly and stay invisible.
    clone.querySelectorAll('animation-reveal').forEach((el) => el.replaceWith(...el.childNodes));
    this.appendChild(clone);
  }
}

customElements.get('quiz-corner-icons') || customElements.define('quiz-corner-icons', QuizCornerIcons);
